<script setup lang="ts">
import { Icon } from '@iconify/vue'
import {
  ElMessage,
  genFileId,
  useFormDisabled,
  useFormItem,
  type UploadFile,
  type UploadFiles,
  type UploadProps,
  type UploadRawFile,
  type UploadUserFile,
} from 'element-plus'
import { computed, ref, watch } from 'vue'
import RlImageThumb from '../common/RlImageThumb.vue'

// v-model
const model = defineModel<UploadUserFile | null>()

// Validate on model change
const { formItem: elFormItem } = useFormItem()

watch(model, (newModel) => {
  elFormItem?.validate?.('change').catch((err) => console.warn(err))
  if (!newModel) {
    uploadRef.value!.clearFiles()
  }
})

// Refs
const uploadRef = ref()
const formDisabled = useFormDisabled()

// Props
const props = withDefaults(
  defineProps<{
    about: string
    deletable?: boolean
    imageUrl?: string
    accept?: string
    maxFileSizeMb?: number
  }>(),
  {
    imageUrl: undefined,
    deletable: true,
    accept: 'image/png, image/jpeg',
    maxFileSizeMb: 10,
  }
)

const fileList = ref<UploadUserFile[]>([])
const setFileToImageUrl = () => {
  const uid = genFileId()
  fileList.value = [
    {
      uid,
      url: props.imageUrl,
      name: uid.toString(),
    },
  ]
}

if (props.imageUrl) {
  setFileToImageUrl()
}

watch(
  () => props.imageUrl,
  (oldImage, newImage) => {
    // Compare with no params to avoid reloading when the image
    // hasn't changed but the query string has (e.g. keys)
    if (oldImage?.split('?')[0] != newImage?.split('?')[0]) {
      setFileToImageUrl()
    }
  }
)

const acceptedTypesText = computed(() =>
  props.accept
    .split(', ')
    .map((mime: string) => `.${mime.split('/')[1]}`)
    .join(', ')
)

/**
 * Handles upload file added.
 *
 * @param uploadFile the file added.
 * @param uploadFiles the file list.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const handleUploadChange: UploadProps['onChange'] = async (uploadFile: UploadFile, uploadFiles: UploadFiles) => {
  const valid = validateAttachment(uploadFile.raw!)
  if (valid) {
    model.value = uploadFile.raw!
  } else {
    uploadRef.value!.clearFiles()
  }
}

/**
 * Handles upload file removed.
 *
 * @param uploadFile the upload file removed.
 * @param uploadFiles the upload file list.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const handleUploadRemove: UploadProps['onRemove'] = async (uploadFile: UploadFile, uploadFiles: UploadFiles) => {
  if (props.imageUrl) {
    // We're editing an existing receipt picture
    if (model.value) {
      // This was a user-added image, re-insert default
      reset()
    } else {
      // The user removed the existing receipt picture, set null
      model.value = null
    }
  } else {
    // We're not editing, reset to undefined
    model.value = undefined
  }
}

/**
 * Handles upload limit exceeded: automatically covers previous file.
 *
 * @param files the file list.
 * @param uploadFile the file added after limit exceeded.
 */

const handleUploadExceed: UploadProps['onExceed'] = async (files: File[]) => {
  uploadRef.value!.clearFiles()
  const file = files[0] as UploadRawFile
  file.uid = genFileId()
  uploadRef.value!.handleStart(file)
}

/**
 * Validates an attached file (must be PNG or JPG, up to 10MB).
 *
 * @param file attached file.
 */
const validateAttachment = (file: File) => {
  const acceptedMimeTypes = props.accept.split(', ')
  if (!acceptedMimeTypes.includes(file.type)) {
    ElMessage.error(`${props.about} picture must be PNG or JPG format.`)
    return false
  } else if (file.size > 10 * 1024 * 1024) {
    ElMessage.error(`${props.about} picture cannot exceed 10MB.`)
    return false
  }
  return true
}

/**
 * (For update only). Resets the field to default.
 */
const reset = () => {
  model.value = undefined
  const uid = genFileId()

  fileList.value.push({
    uid,
    url: props.imageUrl,
    name: uid.toString(),
  })
}
</script>

<template>
  <div class="upload-wrapper flex flex-col gap-spacing-sm">
    <el-upload
      ref="uploadRef"
      v-model:file-list="fileList"
      list-type="picture-card"
      :auto-upload="false"
      :limit="1"
      :on-change="handleUploadChange"
      :on-remove="handleUploadRemove"
      :on-exceed="handleUploadExceed"
      :accept="props.accept"
      :disabled="formDisabled"
      :class="fileList.length > 0 && 'hide-dragger'"
      drag
    >
      <div class="h-full flex flex-col items-center justify-center gap-spacing-2xs text-center">
        <Icon icon="material-symbols:upload-rounded" :width="48" :height="48" />
        <p><span style="color: var(--text-brand)">Upload a file</span> or drag and drop</p>
        <small class="text-xs">Image ({{ acceptedTypesText }}) up to {{ props.maxFileSizeMb }}MB</small>
      </div>
      <template #file="{ file }">
        <RlImageThumb
          v-if="file.url"
          :url="file.url"
          class="w-full"
          :deletable="props.deletable"
          :delete-pending="formDisabled"
          @delete="uploadRef!.handleRemove(file)"
        />
      </template>
    </el-upload>
    <el-alert
      v-if="model === null"
      type="warning"
      :title="`${props.about} image will be deleted.`"
      close-text="Cancel?"
      @close="reset"
    />
  </div>
</template>

<style scoped lang="scss">
@use '@/styles/element/overrides';

:deep() {
  @include overrides.custom-el-upload;
}
</style>
